/** Money and quantities arrive as decimal strings. Number() is used for display formatting only — never arithmetic. */
const fmtCache = new Map<string, Intl.NumberFormat>();

export function money(value: string | number | null | undefined, currency = 'EGP', opts: { compact?: boolean } = {}) {
  if (value === null || value === undefined || value === '') return '—';
  const locale = document.documentElement.lang === 'ar' ? 'ar-EG' : 'en-GB';
  const key = `${locale}|${currency}|${opts.compact ? 'c' : 'f'}`;
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      notation: opts.compact ? 'compact' : 'standard',
      maximumFractionDigits: opts.compact ? 1 : undefined,
    });
    fmtCache.set(key, f);
  }
  return f.format(Number(value));
}

export function num(value: string | number | null | undefined, digits = 0) {
  if (value === null || value === undefined || value === '') return '—';
  return new Intl.NumberFormat(document.documentElement.lang === 'ar' ? 'ar-EG' : 'en-GB', { maximumFractionDigits: digits }).format(Number(value));
}

export function qty(value: string | number | null | undefined) {
  return num(value, 3);
}

export function pct(value: number | null | undefined, digits = 1) {
  if (value === null || value === undefined) return '—';
  return `${(value * 100).toFixed(digits)}%`;
}

export function date(value: string | Date | null | undefined, withTime = false) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  return new Intl.DateTimeFormat(document.documentElement.lang === 'ar' ? 'ar-EG' : 'en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(d);
}

export function relative(value: string | Date | null | undefined) {
  if (!value) return '—';
  const d = typeof value === 'string' ? new Date(value) : value;
  const diff = (d.getTime() - Date.now()) / 1000;
  const rtf = new Intl.RelativeTimeFormat(document.documentElement.lang === 'ar' ? 'ar' : 'en', { numeric: 'auto' });
  const abs = Math.abs(diff);
  if (abs < 60) return rtf.format(Math.round(diff), 'second');
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour');
  if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), 'day');
  return date(d);
}

export const humanize = (s: string | null | undefined) => (s ? s.replace(/_/g, ' ').toLowerCase().replace(/^\w/, (c) => c.toUpperCase()) : '');

export function localName(p: { nameEn?: string | null; nameAr?: string | null } | null | undefined) {
  if (!p) return '';
  return document.documentElement.lang === 'ar' && p.nameAr ? p.nameAr : (p.nameEn ?? '');
}
